import React, { useMemo } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

const SchoolReportCards: React.FC = () => {
  const navigate = useNavigate();
  const { students, loading } = useSchool();

  const classGroups = useMemo(() => {
    const map = new Map<string, { className: string; studentCount: number }>();
    students.forEach((student) => {
      const className = student.className?.trim() || 'Unassigned';
      const existing = map.get(className);
      if (existing) {
        existing.studentCount += 1;
      } else {
        map.set(className, { className, studentCount: 1 });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.className.localeCompare(b.className));
  }, [students]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Report Cards
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/school/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Select a class to view report cards for students in that class.
      </Typography>

      {classGroups.length === 0 ? (
        <Typography>No students are assigned to any classes yet.</Typography>
      ) : (
        <Grid container spacing={3}>
          {classGroups.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.className}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {group.className}
                  </Typography>
                  <Typography color="text.secondary">Students: {group.studentCount}</Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/school/report-cards/class/${encodeURIComponent(group.className)}`)}>
                    View Students
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SchoolReportCards;
